import { getStageFromUrl } from '../../utils/stageUtil';
import { COGNITO_CALLBACK_PATH, COGNITO_SCOPES, EXTERNAL_AUTH_CONFIG_BY_STAGE, ExternalAuthConfig } from '../config/externalAuthConfig';

// Media Processing login through the Cognito Hosted UI: OAuth 2.0 Authorization Code flow
// with PKCE (S256), plus `state` (anti-CSRF) and `nonce` (anti-replay).
//
// Session storage: the id_token is kept in sessionStorage (per tab, cleared on close),
// never in a cookie and never in localStorage. The refresh token is deliberately NOT
// stored, so a session lasts at most the id_token lifetime (1h) before re-login.
//
// The id_token is only decoded here for display and expiry. It came straight from the
// Cognito token endpoint over TLS; the API that receives it as a Bearer token MUST verify
// its signature (API Gateway Cognito User Pool Authorizer).

const PKCE_VERIFIER_KEY = 'cognito_auth_pkce_verifier';
const STATE_KEY = 'cognito_auth_state';
const NONCE_KEY = 'cognito_auth_nonce';
const RETURN_PATH_KEY = 'cognito_auth_return_path';
const SESSION_KEY = 'cognito_session';

export interface CognitoClaims {
  sub: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  'cognito:username'?: string;
  'cognito:groups'?: string[];
  identities?: { providerName: string }[];
  exp: number;
  nonce?: string;
}

export interface CognitoSession {
  idToken: string;
  accessToken: string;
  claims: CognitoClaims;
}

export interface CognitoCallbackResult {
  session: CognitoSession;
  returnPath: string;
}

// Only same-origin absolute paths; reject protocol-relative `//host` (open redirect).
const safeReturnPath = (path: string | null): string => (path && path.startsWith('/') && !path.startsWith('//') ? path : '/');

export const getExternalAuthConfig = (): ExternalAuthConfig | undefined => EXTERNAL_AUTH_CONFIG_BY_STAGE[getStageFromUrl()];

const base64UrlEncode = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const generateRandomToken = (byteLength = 32): string => {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
};

const sha256Base64Url = async (input: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return base64UrlEncode(new Uint8Array(digest));
};

const decodeJwtPayload = (token: string): CognitoClaims | null => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const redirectUri = (): string => `${window.location.origin}${COGNITO_CALLBACK_PATH}`;

/**
 * Redirect the browser to the Cognito Hosted UI (email/password or "Sign in with Federate").
 * The current path is restored after the callback.
 */
export const startExternalLogin = async (config: ExternalAuthConfig): Promise<void> => {
  const verifier = generateRandomToken(64);
  const state = generateRandomToken();
  const nonce = generateRandomToken();
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(NONCE_KEY, nonce);
  sessionStorage.setItem(RETURN_PATH_KEY, `${window.location.pathname}${window.location.search}` || '/');

  const url = new URL(`https://${config.hostedUiDomain}/oauth2/authorize`);
  url.searchParams.append('client_id', config.clientId);
  url.searchParams.append('response_type', 'code');
  url.searchParams.append('scope', COGNITO_SCOPES);
  url.searchParams.append('redirect_uri', redirectUri());
  url.searchParams.append('code_challenge', await sha256Base64Url(verifier));
  url.searchParams.append('code_challenge_method', 'S256');
  url.searchParams.append('state', state);
  url.searchParams.append('nonce', nonce);
  window.location.assign(url.toString());
};

/**
 * Validate and consume the Hosted UI callback: check `state`, exchange `code` +
 * PKCE verifier for tokens, check the id_token `nonce`, then store the session.
 * The one-time values are cleared on every call regardless of outcome.
 */
export const consumeCognitoCallback = async (config: ExternalAuthConfig): Promise<CognitoCallbackResult> => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const returnedState = params.get('state');
  const oauthError = params.get('error');

  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const expectedNonce = sessionStorage.getItem(NONCE_KEY);
  const returnPath = safeReturnPath(sessionStorage.getItem(RETURN_PATH_KEY));
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);

  if (oauthError) {
    throw new Error(`Hosted UI returned error: ${oauthError}`);
  }
  if (!code) {
    throw new Error('Cognito callback is missing code');
  }
  if (!expectedState || returnedState !== expectedState) {
    throw new Error('Cognito callback state mismatch (possible login CSRF)');
  }
  if (!verifier) {
    throw new Error('Missing PKCE verifier');
  }

  const response = await fetch(`https://${config.hostedUiDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  });
  if (!response.ok) {
    throw new Error(`Token exchange failed: HTTP ${response.status}`);
  }
  const tokens = (await response.json()) as { id_token?: string; access_token?: string };
  if (!tokens.id_token || !tokens.access_token) {
    throw new Error('Token response is missing id_token or access_token');
  }

  const claims = decodeJwtPayload(tokens.id_token);
  if (!claims || !expectedNonce || claims.nonce !== expectedNonce) {
    throw new Error('Cognito id_token nonce mismatch (possible token replay)');
  }

  const session: CognitoSession = { idToken: tokens.id_token, accessToken: tokens.access_token, claims };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({ idToken: session.idToken, accessToken: session.accessToken }));
  return { session, returnPath };
};

/** Current Media Processing session, or null when absent or expired. */
export const getCognitoSession = (): CognitoSession | null => {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) {
    return null;
  }
  try {
    const { idToken, accessToken } = JSON.parse(raw) as { idToken: string; accessToken: string };
    const claims = decodeJwtPayload(idToken);
    // A malformed token (missing or non-numeric exp) is treated as expired.
    if (!claims || typeof claims.exp !== 'number' || claims.exp < Math.floor(Date.now() / 1000)) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return { idToken, accessToken, claims };
  } catch {
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }
};

/** Workspace/role groups (ws-{workspace}-{role}) from the id_token. */
export const getCognitoGroups = (session: CognitoSession | null): string[] => session?.claims['cognito:groups'] ?? [];

/** Clear the local session and end the Hosted UI session. */
export const cognitoLogout = (config: ExternalAuthConfig): void => {
  sessionStorage.removeItem(SESSION_KEY);
  const url = new URL(`https://${config.hostedUiDomain}/logout`);
  url.searchParams.append('client_id', config.clientId);
  url.searchParams.append('logout_uri', `${window.location.origin}/`);
  window.location.assign(url.toString());
};
